import { Link } from "@tanstack/react-router"
import { ChevronRight, LayoutGrid, PanelLeftClose } from "lucide-react"
import * as React from "react"
import { Badge } from "@/components/ui/badge"
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarGroup,
	SidebarHeader,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	useSidebar,
} from "@/components/ui/sidebar"
import type {
	LiveBadgeKind,
	NavGroup,
	NavGroupConfig,
	NavPage,
} from "@/config/navigation"
import UserProfileToggle, {
	RailUserProfileToggle,
} from "@/integrations/clerk/user-profile-toggle"
import { cn } from "@/utils/styles.helpers"
import { AllPagesMenu } from "./all-pages-menu"
import {
	EnvironmentSwitcher,
	RailEnvironmentSwitcher,
} from "./environment-switcher"
import { LiveCountBadge } from "./live-count-badge"
import type { NavLocation } from "./nav-model"
import {
	pageHref,
	pageLiveBadge,
	useNavModel,
	usePanelGroupKey,
} from "./nav-model"

function PageBadges({ page }: { page: NavPage }) {
	const liveBadge = pageLiveBadge(page)

	if (liveBadge) {
		return <LiveCountBadge kind={liveBadge} />
	}

	if (page.badge) {
		return (
			<Badge variant="outline" className="ml-auto h-5 px-1.5 text-xs">
				{page.badge}
			</Badge>
		)
	}

	return null
}

function groupLiveBadge(group: NavGroupConfig): LiveBadgeKind | undefined {
	for (const page of group.pages) {
		const liveBadge = pageLiveBadge(page)
		if (liveBadge) return liveBadge
	}

	return undefined
}

const railItemClass =
	"relative flex h-[52px] w-[62px] shrink-0 cursor-pointer flex-col items-center justify-center gap-1 rounded-lg px-0.5 text-rail-foreground outline-hidden transition-colors hover:bg-rail-hover hover:text-rail-accent-foreground focus-visible:ring-2 focus-visible:ring-white/50"

function RailItemBody({
	icon: Icon,
	label,
	liveBadge,
}: {
	icon: NavGroupConfig["icon"]
	label: string
	liveBadge?: LiveBadgeKind
}) {
	return (
		<>
			<span className="relative">
				<Icon className="size-[18px]" />
				{liveBadge && <LiveCountBadge kind={liveBadge} variant="dot" />}
			</span>
			<span className="line-clamp-2 text-center text-[10px] leading-[1.15] font-medium">
				{label}
			</span>
		</>
	)
}

function RailGroupButton({
	group,
	current,
	panelShown,
	onTogglePanel,
	onOpenSinglePage,
}: {
	group: NavGroupConfig
	current: boolean
	panelShown: boolean
	onTogglePanel: (group: NavGroup) => void
	onOpenSinglePage: () => void
}) {
	const className = cn(
		railItemClass,
		panelShown && !current && "text-rail-accent-foreground",
		current &&
			"bg-rail-accent text-rail-accent-foreground hover:bg-rail-accent",
	)
	const body = (
		<RailItemBody
			icon={group.icon}
			label={group.label}
			liveBadge={groupLiveBadge(group)}
		/>
	)
	const [onlyPage] = group.pages

	if (group.pages.length === 1 && onlyPage) {
		return (
			<Link
				to={pageHref(onlyPage)}
				onClick={onOpenSinglePage}
				className={className}
				aria-current={current ? "page" : undefined}
			>
				{body}
			</Link>
		)
	}

	return (
		<button
			type="button"
			onClick={() => onTogglePanel(group.key)}
			className={className}
			aria-expanded={panelShown}
			aria-current={current ? "page" : undefined}
		>
			{body}
		</button>
	)
}

function PagePanel({
	group,
	location,
	onClose,
}: {
	group: NavGroupConfig
	location?: NavLocation
	onClose: () => void
}) {
	return (
		<nav
			aria-label={group.label}
			className="flex h-full w-[220px] shrink-0 flex-col gap-0.5 overflow-y-auto border-r border-sidebar-border bg-sidebar p-2 text-sidebar-foreground"
		>
			<div className="mb-1 flex items-center justify-between pl-2">
				<h2 className="text-[11px] leading-4 font-semibold tracking-[0.07em] text-meta uppercase">
					{group.label}
				</h2>
				<button
					type="button"
					onClick={onClose}
					className="cursor-pointer rounded-md p-1 text-meta hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
					aria-label={`Close ${group.label}`}
					title="Close"
				>
					<PanelLeftClose className="size-4" />
				</button>
			</div>

			{group.pages.map((page) => {
				const active =
					location?.group.key === group.key &&
					location.page.title === page.title

				return (
					<Link
						key={page.title}
						to={pageHref(page)}
						aria-current={active ? "page" : undefined}
						className={cn(
							"group/page flex h-[30px] shrink-0 items-center gap-2 rounded-md px-2 text-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
							active &&
								"bg-sidebar-accent font-medium text-sidebar-accent-foreground shadow-[inset_2px_0_0_0_var(--marker)]",
						)}
					>
						<page.icon
							className={cn(
								"size-[15px] shrink-0 text-meta group-hover/page:text-sidebar-foreground",
								active && "text-sidebar-foreground",
							)}
						/>
						<span className="truncate">{page.title}</span>
						<PageBadges page={page} />
					</Link>
				)
			})}
		</nav>
	)
}

function DesktopNavigation({
	groups,
	location,
	pathname,
}: {
	groups: NavGroupConfig[]
	location?: NavLocation
	pathname: string
}) {
	const { open: panelOpen, setOpen: setPanelOpen } = useSidebar()
	const [allPagesOpen, setAllPagesOpen] = React.useState(false)

	const currentGroup = location?.group
	const followedGroupKey =
		currentGroup && currentGroup.pages.length > 1 ? currentGroup.key : undefined
	const [panelGroupKey, setPanelGroupKey] = usePanelGroupKey(followedGroupKey)

	// biome-ignore lint/correctness/useExhaustiveDependencies: every navigation brings the panel back to the current route's group
	React.useEffect(() => {
		if (followedGroupKey) {
			setPanelGroupKey(followedGroupKey)
		}
	}, [followedGroupKey, pathname, setPanelGroupKey])

	const multiPageGroups = groups.filter((group) => group.pages.length > 1)
	const panelGroup =
		multiPageGroups.find((group) => group.key === panelGroupKey) ??
		multiPageGroups[0]
	// On a single-page section the panel only shows after a group is opened by hand.
	const [openedByHand, setOpenedByHand] = React.useState(false)
	// biome-ignore lint/correctness/useExhaustiveDependencies: a navigation resets the hand-opened state
	React.useEffect(() => {
		setOpenedByHand(false)
	}, [pathname])
	const panelShown =
		panelOpen &&
		panelGroup !== undefined &&
		(followedGroupKey !== undefined || openedByHand)

	const togglePanel = (key: NavGroup) => {
		if (panelShown && panelGroup?.key === key) {
			setPanelOpen(false)
			return
		}

		setPanelGroupKey(key)
		setPanelOpen(true)
		setOpenedByHand(true)
	}

	return (
		<aside className="sticky top-0 hidden h-svh shrink-0 md:flex">
			<nav
				aria-label="Main"
				className="flex h-full w-[72px] flex-col items-center border-r border-rail-border bg-rail py-2 text-rail-foreground"
			>
				<RailEnvironmentSwitcher />

				<button
					type="button"
					onClick={() => setAllPagesOpen(true)}
					className={cn(
						railItemClass,
						"mt-2",
						allPagesOpen && "bg-rail-accent text-rail-accent-foreground",
					)}
					aria-haspopup="dialog"
				>
					<RailItemBody icon={LayoutGrid} label="All pages" />
				</button>

				<div className="my-2 w-8 shrink-0 border-t border-rail-border" />

				<div className="flex min-h-0 w-full flex-1 flex-col items-center gap-0.5 overflow-y-auto overflow-x-hidden">
					{groups.map((group) => (
						<RailGroupButton
							key={group.key}
							group={group}
							current={currentGroup?.key === group.key}
							panelShown={panelShown && panelGroup?.key === group.key}
							onTogglePanel={togglePanel}
							onOpenSinglePage={() => setPanelOpen(false)}
						/>
					))}
				</div>

				<div className="pt-2">
					<RailUserProfileToggle />
				</div>
			</nav>

			{panelShown && panelGroup && (
				<PagePanel
					group={panelGroup}
					location={location}
					onClose={() => setPanelOpen(false)}
				/>
			)}

			<AllPagesMenu
				open={allPagesOpen}
				onOpenChange={setAllPagesOpen}
				groups={groups}
				location={location}
			/>
		</aside>
	)
}

function MobileGroupSection({
	group,
	location,
	onNavigate,
}: {
	group: NavGroupConfig
	location?: NavLocation
	onNavigate: () => void
}) {
	const [onlyPage] = group.pages
	if (group.pages.length === 1 && onlyPage) {
		return (
			<SidebarMenuItem className="list-none">
				<SidebarMenuButton
					asChild
					isActive={location?.group.key === group.key}
					className="font-medium"
				>
					<Link to={pageHref(onlyPage)} onClick={onNavigate}>
						<group.icon className="size-4" />
						<span>{group.label}</span>
						<PageBadges page={onlyPage} />
					</Link>
				</SidebarMenuButton>
			</SidebarMenuItem>
		)
	}

	return (
		<Collapsible defaultOpen={location?.group.key === group.key} asChild>
			<SidebarGroup className="p-0">
				<SidebarMenu>
					<SidebarMenuItem>
						<CollapsibleTrigger asChild>
							<SidebarMenuButton className="group/trigger cursor-pointer font-medium">
								<group.icon className="size-4" />
								<span>{group.label}</span>
								<ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/trigger:rotate-90" />
							</SidebarMenuButton>
						</CollapsibleTrigger>
					</SidebarMenuItem>
				</SidebarMenu>
				<CollapsibleContent>
					<SidebarMenu className="py-1 pl-3">
						{group.pages.map((page) => (
							<SidebarMenuItem key={page.title}>
								<SidebarMenuButton
									asChild
									isActive={
										location?.group.key === group.key &&
										location.page.title === page.title
									}
								>
									<Link to={pageHref(page)} onClick={onNavigate}>
										<page.icon className="size-4" />
										<span>{page.title}</span>
										<PageBadges page={page} />
									</Link>
								</SidebarMenuButton>
							</SidebarMenuItem>
						))}
					</SidebarMenu>
				</CollapsibleContent>
			</SidebarGroup>
		</Collapsible>
	)
}

function MobileNavigation({
	groups,
	location,
}: {
	groups: NavGroupConfig[]
	location?: NavLocation
}) {
	const { setOpenMobile } = useSidebar()

	return (
		<Sidebar>
			<SidebarHeader>
				<EnvironmentSwitcher />
			</SidebarHeader>
			<SidebarContent className="gap-0.5 px-2">
				{groups.map((group) => (
					<MobileGroupSection
						key={group.key}
						group={group}
						location={location}
						onNavigate={() => setOpenMobile(false)}
					/>
				))}
			</SidebarContent>
			<SidebarFooter>
				<UserProfileToggle />
			</SidebarFooter>
		</Sidebar>
	)
}

/**
 * The admin navigation: on desktop a rail of group icons with the selected
 * group's page panel and the all-pages menu; on mobile a sheet listing the
 * groups as collapsible sections.
 */
export function AppSidebar() {
	const { isMobile } = useSidebar()
	const { groups, location, pathname } = useNavModel()

	if (isMobile) {
		return <MobileNavigation groups={groups} location={location} />
	}

	return (
		<DesktopNavigation
			groups={groups}
			location={location}
			pathname={pathname}
		/>
	)
}
